import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, ApiError, type OrderSummary } from '../lib/api'
import { useAsync, useCart, useStore } from '../lib/store'
import { Img, Loading, Shell } from '../components/Layout'
import { PasswordInput } from '../components/PasswordInput'

/* --------------------------------------------------------------- sign in */

export function Login() {
  const { user, setUser, userLoading } = useStore()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (userLoading) return <Shell><Loading /></Shell>
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : '/account'} replace />

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const r = mode === 'login' ? await api.login(email, password) : await api.register(email, password, name)
      setUser(r.user)
      navigate(r.user.role === 'admin' ? '/admin' : '/account', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        // Spell out field problems (e.g. an email the browser accepted but the
        // server did not) instead of a bare "check the highlighted fields".
        const detail = err.fields?.map((f) => f.message).join(' ')
        setError(detail || (err.status >= 500 ? 'We could not complete that just now. Please try again in a moment.' : err.message))
      } else {
        setError(mode === 'login' ? 'Could not sign you in.' : 'Could not create your account.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell>
      <section className="section" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <h1 className="display h-md" style={{ textAlign: 'center' }}>
            {mode === 'login' ? 'Sign in' : 'Create an account'}
          </h1>

          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {mode === 'register' && (
              <div className="field">
                <label htmlFor="ac-name">Your name</label>
                <input id="ac-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={100} />
              </div>
            )}
            <div className="field">
              <label htmlFor="ac-email">Email address</label>
              <input id="ac-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </div>
            <div className="field">
              <label htmlFor="ac-pw">Password</label>
              <PasswordInput
                id="ac-pw"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
              {mode === 'register' ? (
                <span className="small">At least 8 characters.</span>
              ) : (
                <Link to="/forgot-password" className="small" style={{ alignSelf: 'flex-end', textDecoration: 'underline' }}>
                  Forgot your password?
                </Link>
              )}
            </div>

            {error && <div className="notice notice-error">{error}</div>}

            <button className="btn btn-solid" style={{ padding: '14px 24px' }} disabled={busy}>
              {busy ? 'Just a moment…' : mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>

          <button
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login')
              setError('')
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--muted)', textDecoration: 'underline' }}
          >
            {mode === 'login' ? 'No account? Create one' : 'Already have an account? Sign in'}
          </button>
        </div>
      </section>
    </Shell>
  )
}

/* ------------------------------------------------------- password reset */

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      setSent((await api.forgotPassword(email)).message)
    } catch (err) {
      setError(err instanceof ApiError ? err.fields?.map((f) => f.message).join(' ') || err.message : 'Could not send a reset link.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell>
      <section className="section" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <h1 className="display h-md" style={{ textAlign: 'center' }}>
            Reset your password
          </h1>
          {sent ? (
            <div className="notice notice-ok">{sent}</div>
          ) : (
            <>
              <p className="small" style={{ textAlign: 'center', margin: 0 }}>
                Enter the email address on your account and we will send you a link to choose a new password.
              </p>
              <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="field">
                  <label htmlFor="fp-email">Email address</label>
                  <input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                </div>
                {error && <div className="notice notice-error">{error}</div>}
                <button className="btn btn-solid" style={{ padding: '14px 24px' }} disabled={busy}>
                  {busy ? 'Just a moment…' : 'Send reset link'}
                </button>
              </form>
            </>
          )}
          <Link to="/login" className="small" style={{ textAlign: 'center', textDecoration: 'underline' }}>
            Back to sign in
          </Link>
        </div>
      </section>
    </Shell>
  )
}

export function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const { setUser } = useStore()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const r = await api.resetPassword(token, password)
      setUser(r.user)
      navigate(r.user.role === 'admin' ? '/admin' : '/account', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.fields?.map((f) => f.message).join(' ') || err.message : 'Could not reset your password.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell>
      <section className="section" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <h1 className="display h-md" style={{ textAlign: 'center' }}>
            Choose a new password
          </h1>
          {!token ? (
            <div className="notice notice-error">This reset link is incomplete. Open the link from your email again, or request a new one.</div>
          ) : (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="field">
                <label htmlFor="rp-pw">New password</label>
                <PasswordInput id="rp-pw" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                <span className="small">At least 8 characters. Every device signed in to your account will be signed out.</span>
              </div>
              {error && <div className="notice notice-error">{error}</div>}
              <button className="btn btn-solid" style={{ padding: '14px 24px' }} disabled={busy}>
                {busy ? 'Just a moment…' : 'Set new password'}
              </button>
            </form>
          )}
          <Link to="/forgot-password" className="small" style={{ textAlign: 'center', textDecoration: 'underline' }}>
            Request a new link
          </Link>
        </div>
      </section>
    </Shell>
  )
}

/* -------------------------------------------------------------- account */

export function Account() {
  const { user, userLoading, setUser, money } = useStore()
  const navigate = useNavigate()
  const { data: orders, loading } = useAsync(() => (user ? api.myOrders() : Promise.resolve([])), [user?.id])

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [pwMsg, setPwMsg] = useState('')
  const [pwErr, setPwErr] = useState('')

  if (userLoading) return <Shell><Loading /></Shell>
  if (!user) return <Navigate to="/login" replace />

  async function changePassword(e: React.FormEvent) {
    e.preventDefault()
    setPwMsg('')
    setPwErr('')
    try {
      const r = await api.changePassword(current, next)
      setPwMsg(r.message)
      setCurrent('')
      setNext('')
    } catch (err) {
      setPwErr(err instanceof ApiError ? err.message : 'Could not update your password.')
    }
  }

  return (
    <Shell>
      <section className="section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 30 }}>
          <div>
            <h1 className="display h-md" style={{ marginBottom: 6 }}>
              {user.name || 'Your account'}
            </h1>
            <p className="small" style={{ margin: 0 }}>
              {user.email}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {user.role === 'admin' && (
              <Link to="/admin" className="btn btn-sm">
                Admin panel
              </Link>
            )}
            <button
              className="btn btn-sm"
              onClick={async () => {
                await api.logout().catch(() => undefined)
                setUser(null)
                navigate('/')
              }}
            >
              Sign out
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 'clamp(20px,3vw,40px)', alignItems: 'start' }}>
          <div>
            <h2 className="panel-title">Your orders</h2>
            {loading ? (
              <Loading />
            ) : (orders ?? []).length === 0 ? (
              <p className="small" style={{ margin: 0 }}>
                No orders yet. <Link to="/shop" style={{ textDecoration: 'underline' }}>Start shopping</Link>.
              </p>
            ) : (
              <div className="scroll-x">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orders ?? []).map((o) => (
                      <tr key={o.reference}>
                        <td>
                          <Link to={`/order/${o.reference}`} style={{ textDecoration: 'underline' }}>
                            {o.reference}
                          </Link>
                        </td>
                        <td>{new Date(o.createdAt + 'Z').toLocaleDateString('en-GB')}</td>
                        <td>
                          <span className="pill">{o.status}</span>
                        </td>
                        <td>{money(o.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="panel">
            <h2 className="panel-title">Change password</h2>
            <form onSubmit={changePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="field">
                <label htmlFor="pw-cur">Current password</label>
                <PasswordInput id="pw-cur" required value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
              </div>
              <div className="field">
                <label htmlFor="pw-new">New password</label>
                <PasswordInput id="pw-new" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
              </div>
              {pwErr && <div className="notice notice-error">{pwErr}</div>}
              {pwMsg && <div className="notice notice-ok">{pwMsg}</div>}
              <button className="btn btn-sm" style={{ alignSelf: 'flex-start' }}>
                Update password
              </button>
            </form>
          </div>
        </div>
      </section>
    </Shell>
  )
}

/* ------------------------------------------------------ order + tracking */

export function OrderPage() {
  const { reference = '' } = useParams()
  const [params] = useSearchParams()
  const emailParam = params.get('email') ?? ''
  const { money } = useStore()
  const { clear } = useCart()

  // Stripe returns the shopper here with ?paid=1 once the session completes.
  const justPaid = params.get('paid') === '1'
  const cleared = useRef(false)

  const [email, setEmail] = useState(emailParam)
  const [submitted, setSubmitted] = useState(!!emailParam)

  const { data, loading, error, reload } = useAsync(
    () => (submitted || !emailParam ? api.order(reference, email) : Promise.reject(new Error('need email'))),
    [reference, submitted ? email : '']
  )

  // Stripe tells us about the payment by webhook, usually within seconds of
  // the redirect. Check back a few times rather than show "unpaid" for good.
  const polls = useRef(0)
  const settling = justPaid && data?.paymentStatus === 'unpaid' && data.status === 'pending'
  useEffect(() => {
    if (!settling || polls.current >= 10) return
    const t = setTimeout(() => {
      polls.current++
      reload()
    }, 3000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settling, data])

  // The bag was deliberately kept through the redirect in case payment was
  // abandoned; now that the order is on screen, it has done its job.
  useEffect(() => {
    if (!justPaid || !data || cleared.current) return
    cleared.current = true
    clear()
  }, [justPaid, data, clear])

  if (loading && !data) return <Shell><Loading label="Loading order" /></Shell>

  if (error || !data) {
    return (
      <Shell>
        <section className="section" style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h1 className="display h-md" style={{ textAlign: 'center' }}>
              Find your order
            </h1>
            <p className="small" style={{ textAlign: 'center', margin: 0 }}>
              {error && error !== 'need email' ? error : 'Enter the email address used for this order.'}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                setSubmitted(true)
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <div className="field">
                <label htmlFor="ord-email">Email address</label>
                <input id="ord-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <button className="btn btn-solid" style={{ padding: '13px 24px' }}>
                View order
              </button>
            </form>
          </div>
        </section>
      </Shell>
    )
  }

  return (
    <Shell>
      <section className="section" style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
          <div className="eyebrow-rose">Order {data.reference}</div>
          <h1 className="display h-md">{orderHeading(data, justPaid)}</h1>
          {settling && (
            <p className="small" style={{ margin: 0 }}>
              We are confirming your payment with our payment provider. This page updates once it clears.
            </p>
          )}
          {!justPaid && data.status === 'pending' && data.paymentStatus === 'unpaid' && (
            <p className="small" style={{ margin: 0 }}>
              We have not received payment for this order, so it has not been confirmed. If you completed payment, it can take a minute to show here.
            </p>
          )}
          <p className="small" style={{ margin: 0 }}>
            Placed {new Date(data.createdAt + 'Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} ·{' '}
            <span className="pill" style={{ marginLeft: 4 }}>{data.status}</span>
          </p>
        </div>

        <div style={{ background: 'var(--stone)', padding: 'clamp(20px,2.6vw,32px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {data.items.map((i) => (
            <div key={i.slug} style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) auto', gap: 14, alignItems: 'center' }}>
              <span style={{ display: 'block', aspectRatio: '4 / 5', background: 'var(--stone-2)', overflow: 'hidden' }}>
                <Img src={i.image} alt={i.name} label="" />
              </span>
              <span style={{ fontSize: 13 }}>
                {i.name}
                <br />
                <span className="small">× {i.qty}</span>
              </span>
              <span style={{ fontSize: 13 }}>{money(i.unitPrice * i.qty)}</span>
            </div>
          ))}

          <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
              <span>Subtotal</span>
              <span style={{ color: 'var(--ink)' }}>{money(data.subtotal)}</span>
            </div>
            {data.discount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
                <span>Code {data.discountCode}</span>
                <span style={{ color: 'var(--ink)' }}>−{money(data.discount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
              <span>Delivery</span>
              <span style={{ color: 'var(--ink)' }}>{data.shipping ? money(data.shipping) : 'Free'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, marginTop: 6 }}>
              <span>Total</span>
              <span style={{ fontFamily: 'var(--serif)', fontSize: 22 }}>{money(data.total)}</span>
            </div>
          </div>
        </div>

        {(data.shippedAt || data.trackingNumber) && (
          <div style={{ marginTop: 26 }}>
            <h2 className="panel-title">Delivery</h2>
            <p className="small" style={{ margin: 0, lineHeight: 1.9 }}>
              {data.shippedAt && <>Dispatched {fmtDay(data.shippedAt)}<br /></>}
              {data.deliveredAt && <>Delivered {fmtDay(data.deliveredAt)}<br /></>}
              {data.trackingNumber && (
                <>
                  Tracking: {data.trackingCarrier ? `${data.trackingCarrier} ` : ''}
                  <strong>{data.trackingNumber}</strong>
                </>
              )}
            </p>
          </div>
        )}

        <div style={{ marginTop: 26 }}>
          <h2 className="panel-title">Delivering to</h2>
          <p className="small" style={{ margin: 0, lineHeight: 1.9 }}>
            {data.customerName}
            <br />
            {data.address.join(', ')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 30, flexWrap: 'wrap' }}>
          <Link to="/shop" className="btn btn-sm">
            Continue shopping
          </Link>
          <Link to="/account" className="btn btn-sm">
            Your account
          </Link>
        </div>
      </section>
    </Shell>
  )
}

function orderHeading(order: OrderSummary, justPaid: boolean) {
  if (order.status === 'cancelled') return 'This order was cancelled.'
  if (order.status === 'refunded') return 'This order was refunded.'
  if (order.paymentStatus === 'paid') return 'Thank you for your order.'
  return justPaid ? 'Confirming your payment…' : 'Awaiting payment.'
}

/** SQLite writes naive UTC timestamps; mark them so they localise correctly. */
const fmtDay = (iso: string) =>
  new Date(iso.includes('Z') ? iso : iso.replace(' ', 'T') + 'Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

export function TrackOrder() {
  const [reference, setReference] = useState('')
  const navigate = useNavigate()

  return (
    <Shell>
      <section className="section" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <h1 className="display h-md" style={{ textAlign: 'center' }}>
            Track your order
          </h1>
          <p className="small" style={{ textAlign: 'center', margin: 0 }}>
            Enter the reference from your confirmation email.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              navigate(`/order/${reference.trim().toUpperCase()}`)
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div className="field">
              <label htmlFor="tr-ref">Order reference</label>
              <input id="tr-ref" required placeholder="LUM-XXXXX-XXXX" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <button className="btn btn-solid" style={{ padding: '13px 24px' }}>
              Find order
            </button>
          </form>
        </div>
      </section>
    </Shell>
  )
}

export function NotFound() {
  return (
    <Shell>
      <section className="section" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '80px 20px' }}>
        <div className="eyebrow-rose">404</div>
        <h1 className="display h-lg">That page has moved on.</h1>
        <p className="small" style={{ margin: 0, maxWidth: '40ch' }}>
          The link may be out of date. The shop, the journal and the quiz are all still where you left them.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link to="/" className="btn">
            Home
          </Link>
          <Link to="/shop" className="btn">
            Shop
          </Link>
        </div>
      </section>
    </Shell>
  )
}
